import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";

import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";
import {
  AI_EXPLAINED_MAX_QUESTION_CHARS,
  askAIExplained,
} from "../api/aiExhibitAskClient";
import { getAIExhibitBatch6Copy } from "../content/aiExhibitBatch6Content";

export default function AskAIAboutAIScreen({
  language = "en",
  audioEnabled = false,
  visitorUserId,
  clientSessionId,
  askConversationStarted = false,
  onAskConversationStarted,
}) {
  const t = getAIExhibitBatch6Copy(language).askAI;
  const abortRef = useRef(null);
  const answerPanelRef = useRef(null);
  const audioEnabledRef = useRef(audioEnabled);
  const ttsQueueRef = useRef([]);
  const ttsAudioRef = useRef(null);
  const ttsPlayingRef = useRef(false);

  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [typedQuestion, setTypedQuestion] = useState("");
  const [lastQuestion, setLastQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const playNextTtsChunk = useCallback(() => {
    if (ttsPlayingRef.current) return;

    const nextUrl = ttsQueueRef.current.shift();
    if (!nextUrl) return;
    if (typeof Audio === "undefined") return;

    if (!ttsAudioRef.current) {
      ttsAudioRef.current = new Audio();
    }

    ttsPlayingRef.current = true;
    ttsAudioRef.current.src = nextUrl;
    ttsAudioRef.current.onended = () => {
      ttsPlayingRef.current = false;
      playNextTtsChunk();
    };
    ttsAudioRef.current.onerror = (err) => {
      console.warn("[AI EXPLAINED TTS] audio playback error:", err);
      ttsPlayingRef.current = false;
      playNextTtsChunk();
    };
    ttsAudioRef.current.play().catch((err) => {
      console.warn("[AI EXPLAINED TTS] audio play() rejected:", err);
      ttsPlayingRef.current = false;
      playNextTtsChunk();
    });
  }, []);

  const enqueueTtsChunk = useCallback(
    (url) => {
      if (!url) return;
      ttsQueueRef.current.push(url);
      if (!ttsPlayingRef.current) {
        playNextTtsChunk();
      }
    },
    [playNextTtsChunk]
  );

  const stopTtsStream = useCallback(() => {
    ttsQueueRef.current = [];

    if (ttsAudioRef.current) {
      try {
        ttsAudioRef.current.pause();
        ttsAudioRef.current.src = "";
      } catch (_e) {
        // no-op
      }
    }

    ttsPlayingRef.current = false;
  }, []);

  useEffect(() => {
    audioEnabledRef.current = audioEnabled;

    if (!audioEnabled) {
      stopTtsStream();
    }
  }, [audioEnabled, stopTtsStream]);

  useEffect(
    () => () => {
      try {
        abortRef.current?.abort();
      } catch (_e) {
        // no-op
      }
      stopTtsStream();
    },
    [stopTtsStream]
  );

  const submitQuestion = async ({
    question,
    curatedQuestionId = null,
    questionSource = "typed",
  }) => {
    const cleanQuestion = String(question || "").trim();
    if (!cleanQuestion || busy) return;

    try {
      abortRef.current?.abort();
    } catch (_e) {
      // no-op
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setSelectedQuestionId(curatedQuestionId);
    setLastQuestion(cleanQuestion);
    setAnswer("");
    setError("");
    setBusy(true);
    stopTtsStream();

    // Phase 7.3.1: bring the response area near the top once when an ask starts,
    // leaving room for the fixed exhibit toolbar. Do not chase streamed text.
    setTimeout(() => {
      const answerPanel = answerPanelRef.current;
      if (answerPanel?.style) {
        answerPanel.style.scrollMarginTop = "96px";
      }
      answerPanel?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }, 0);

    let liveText = "";

    try {
      const response = await askAIExplained({
        userId: visitorUserId,
        clientSessionId,
        question: cleanQuestion,
        language,
        newDialog: !askConversationStarted,
        curatedQuestionId,
        questionSource,
        speakerMuted: !audioEnabled,
        signal: controller.signal,
        onEvent: (event) => {
          if (event?.type === "text_delta") {
            liveText += event.delta || "";
            setAnswer(liveText);
          } else if (event?.type === "text_done") {
            const finalText = event.text || liveText;
            if (finalText) setAnswer(finalText);
          }

          if (
            event?.type === "audio_chunk" &&
            audioEnabledRef.current &&
            event.url
          ) {
            enqueueTtsChunk(event.url);
          }
        },
      });

      const finalText =
        response?.["AI response"] ||
        response?.["Formatted AI response"] ||
        liveText;

      if (finalText) {
        setAnswer(finalText);
        onAskConversationStarted?.();

        if (questionSource === "typed") {
          setTypedQuestion("");
        }
      } else {
        throw new Error("AI response did not contain text.");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("[AI EXPLAINED] ask failed:", err);
      setError(err?.message || "Ask AI failed");
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setBusy(false);
    }
  };

  const askTypedQuestion = () => {
    submitQuestion({
      question: typedQuestion,
      curatedQuestionId: null,
      questionSource: "typed",
    });
  };

  return (
    <View style={{ padding: 28 }}>
      <Text
        style={{
          color: C.accent,
          fontSize: 13,
          fontWeight: "900",
          letterSpacing: 1.2,
        }}
      >
        {t.eyebrow}
      </Text>

      <Text
        style={{
          color: C.text,
          fontSize: 44,
          lineHeight: 50,
          fontWeight: "900",
          marginTop: 8,
        }}
      >
        {t.title}
      </Text>

      <Text
        style={{
          color: C.muted,
          fontSize: 18,
          lineHeight: 27,
          marginTop: 8,
          maxWidth: 980,
        }}
      >
        {t.subtitle}
      </Text>

      <View
        style={{
          marginTop: 24,
          padding: 20,
          borderRadius: 18,
          borderWidth: 1,
          borderColor: C.accent,
          backgroundColor: C.surfaceRaised,
        }}
      >
        <Text style={{ color: C.text, fontSize: 20, fontWeight: "900" }}>
          {t.openLabel}
        </Text>

        <Text
          style={{
            color: C.muted,
            fontSize: 14,
            lineHeight: 22,
            marginTop: 6,
          }}
        >
          {t.openHint}
        </Text>

        <View
          style={{
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
            gap: 10,
            marginTop: 14,
          }}
        >
          <TextInput
            value={typedQuestion}
            onChangeText={setTypedQuestion}
            onSubmitEditing={askTypedQuestion}
            editable={!busy}
            maxLength={AI_EXPLAINED_MAX_QUESTION_CHARS}
            placeholder={t.placeholder}
            placeholderTextColor={C.muted}
            returnKeyType="send"
            autoCapitalize="sentences"
            autoCorrect
            accessibilityLabel={t.openLabel}
            style={{
              flex: 1,
              minWidth: 430,
              minHeight: 60,
              paddingHorizontal: 17,
              paddingVertical: 14,
              borderRadius: 15,
              borderWidth: 2,
              borderColor: C.border,
              backgroundColor: C.surface,
              color: C.text,
              fontSize: 18,
              outlineStyle: "none",
            }}
          />

          <Pressable
            onPress={askTypedQuestion}
            disabled={busy || !typedQuestion.trim()}
            style={({ pressed }) => ({
              minWidth: 120,
              minHeight: 60,
              paddingHorizontal: 22,
              paddingVertical: 14,
              borderRadius: 15,
              borderWidth: 1,
              borderColor: C.accent,
              backgroundColor: pressed ? C.surface : C.accentSoft,
              alignItems: "center",
              justifyContent: "center",
              opacity: busy || !typedQuestion.trim() ? 0.45 : 1,
            })}
          >
            <Text style={{ color: C.text, fontSize: 18, fontWeight: "900" }}>
              {t.askButton} →
            </Text>
          </Pressable>
        </View>

        <Text
          style={{
            color: C.muted,
            fontSize: 12,
            marginTop: 7,
            textAlign: "right",
          }}
        >
          {typedQuestion.length}/{AI_EXPLAINED_MAX_QUESTION_CHARS} {t.characters}
        </Text>
      </View>

      <Text
        style={{
          color: C.text,
          fontSize: 19,
          fontWeight: "900",
          marginTop: 22,
        }}
      >
        {t.curatedLabel}
      </Text>

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 12,
          marginTop: 12,
        }}
      >
        {t.questions.map((item) => {
          const active = item.id === selectedQuestionId;

          return (
            <Pressable
              key={item.id}
              onPress={() =>
                submitQuestion({
                  question: item.text,
                  curatedQuestionId: item.id,
                  questionSource: "curated",
                })
              }
              disabled={busy}
              style={({ pressed }) => ({
                width: "48.7%",
                minWidth: 330,
                flexGrow: 1,
                minHeight: 92,
                padding: 17,
                borderRadius: 16,
                borderWidth: active ? 2 : 1,
                borderColor: active ? C.accent : C.border,
                backgroundColor:
                  active || pressed ? C.accentSoft : C.surfaceRaised,
                opacity: busy && !active ? 0.55 : 1,
                justifyContent: "center",
              })}
            >
              <Text
                style={{
                  color: C.text,
                  fontSize: 18,
                  lineHeight: 25,
                  fontWeight: "900",
                }}
              >
                {item.text}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View
        ref={answerPanelRef}
        style={{
          marginTop: 20,
          minHeight: 190,
          padding: 22,
          borderRadius: 19,
          borderWidth: 1,
          borderColor: error ? C.warning : answer || busy ? C.accent : C.border,
          backgroundColor: C.surfaceRaised,
        }}
      >
        {lastQuestion ? (
          <View
            style={{
              marginBottom: 14,
              paddingBottom: 14,
              borderBottomWidth: 1,
              borderBottomColor: C.border,
            }}
          >
            <Text style={{ color: C.muted, fontSize: 12, fontWeight: "900" }}>
              {t.youAsked.toUpperCase()}
            </Text>
            <Text
              style={{
                color: C.text,
                fontSize: 17,
                lineHeight: 25,
                fontWeight: "800",
                marginTop: 5,
              }}
            >
              {lastQuestion}
            </Text>
          </View>
        ) : null}

        <Text
          style={{
            color: error ? C.warning : C.accent,
            fontSize: 13,
            fontWeight: "900",
            letterSpacing: 0.8,
          }}
        >
          {error ? t.errorTitle.toUpperCase() : t.answerLabel.toUpperCase()}
        </Text>

        {error ? (
          <Text
            style={{
              color: C.text,
              fontSize: 17,
              lineHeight: 27,
              marginTop: 12,
            }}
          >
            {t.errorBody}
          </Text>
        ) : answer ? (
          <>
            <Text
              style={{
                color: C.text,
                fontSize: 21,
                lineHeight: 32,
                marginTop: 12,
              }}
            >
              {answer}
            </Text>
            {!busy ? (
              <Text
                style={{
                  color: C.muted,
                  fontSize: 14,
                  lineHeight: 21,
                  marginTop: 14,
                }}
              >
                {t.another}
              </Text>
            ) : null}
          </>
        ) : (
          <Text
            style={{
              color: C.muted,
              fontSize: 18,
              lineHeight: 28,
              marginTop: 12,
            }}
          >
            {busy ? t.waiting : t.choosePrompt}
          </Text>
        )}
      </View>

      <View
        style={{
          marginTop: 16,
          padding: 15,
          borderRadius: 14,
          borderWidth: 1,
          borderColor: C.border,
          backgroundColor: C.accentSoft,
        }}
      >
        <Text style={{ color: C.muted, fontSize: 14, lineHeight: 22 }}>
          {t.phaseNote}
        </Text>
      </View>
    </View>
  );
}
